/**
 * Canonical registry names stay `*_workflow` (backend/API). LLM-facing
 * aliases are `*_routine`. Chat adapters must accept both generations.
 */

export type RoutineToolNamePair = {
  readonly canonical: string;
  readonly exposed: string;
};

/** Mirrors the backend LLM-name map even when a pair is not yet wired into an adapter. */
export const ROUTINE_TOOL_NAMES = {
  create: { canonical: 'create_workflow', exposed: 'create_routine' },
  createScheduled: {
    canonical: 'create_scheduled_workflow',
    exposed: 'create_scheduled_routine',
  },
  update: { canonical: 'update_workflow', exposed: 'update_routine' },
  list: { canonical: 'list_workflows', exposed: 'list_routines' },
  search: { canonical: 'search_workflows', exposed: 'search_routines' },
  getRecordedContext: {
    canonical: 'get_recorded_workflow_context',
    exposed: 'get_recorded_routine_context',
  },
  schedule: { canonical: 'schedule_workflow', exposed: 'schedule_routine' },
  setEventTriggers: {
    canonical: 'set_workflow_event_triggers',
    exposed: 'set_routine_event_triggers',
  },
  share: { canonical: 'share_workflow', exposed: 'share_routine' },
  unshare: { canonical: 'unshare_workflow', exposed: 'unshare_routine' },
} as const satisfies Record<string, RoutineToolNamePair>;

export const routineToolNameSet = (...pairs: RoutineToolNamePair[]): Set<string> =>
  new Set(pairs.flatMap((pair) => [pair.canonical, pair.exposed]));

export const CREATE_ROUTINE_TOOL_NAMES = routineToolNameSet(
  ROUTINE_TOOL_NAMES.create,
  ROUTINE_TOOL_NAMES.createScheduled
);

export const CREATE_OR_UPDATE_ROUTINE_TOOL_NAMES = routineToolNameSet(
  ROUTINE_TOOL_NAMES.create,
  ROUTINE_TOOL_NAMES.createScheduled,
  ROUTINE_TOOL_NAMES.update
);

export const SCHEDULE_ROUTINE_TOOL_NAMES = routineToolNameSet(
  ROUTINE_TOOL_NAMES.schedule
);

export const EVENT_TRIGGER_ROUTINE_TOOL_NAMES = routineToolNameSet(
  ROUTINE_TOOL_NAMES.setEventTriggers
);

export const SCHEDULE_OR_TRIGGER_ROUTINE_TOOL_NAMES = routineToolNameSet(
  ROUTINE_TOOL_NAMES.schedule,
  ROUTINE_TOOL_NAMES.setEventTriggers
);

export const SHARE_ROUTINE_TOOL_NAMES = routineToolNameSet(
  ROUTINE_TOOL_NAMES.share,
  ROUTINE_TOOL_NAMES.unshare
);

export const OPENABLE_ROUTINE_TOOL_NAMES = routineToolNameSet(
  ROUTINE_TOOL_NAMES.create,
  ROUTINE_TOOL_NAMES.createScheduled,
  ROUTINE_TOOL_NAMES.update,
  ROUTINE_TOOL_NAMES.schedule,
  ROUTINE_TOOL_NAMES.setEventTriggers,
  ROUTINE_TOOL_NAMES.share,
  ROUTINE_TOOL_NAMES.unshare
);

export const SILENT_CANVAS_ROUTINE_TOOL_NAMES = routineToolNameSet(
  ROUTINE_TOOL_NAMES.schedule,
  ROUTINE_TOOL_NAMES.setEventTriggers,
  ROUTINE_TOOL_NAMES.share,
  ROUTINE_TOOL_NAMES.unshare
);

export const isCreateRoutineTool = (name: string): boolean =>
  CREATE_ROUTINE_TOOL_NAMES.has(name);

export const isCreateOrUpdateRoutineTool = (name: string): boolean =>
  CREATE_OR_UPDATE_ROUTINE_TOOL_NAMES.has(name);

export const isOpenableRoutineTool = (name: string): boolean =>
  OPENABLE_ROUTINE_TOOL_NAMES.has(name);

export const isSilentCanvasRoutineTool = (name: string): boolean =>
  SILENT_CANVAS_ROUTINE_TOOL_NAMES.has(name);

export const isScheduleRoutineTool = (name: string): boolean =>
  SCHEDULE_ROUTINE_TOOL_NAMES.has(name);

export const isEventTriggerRoutineTool = (name: string): boolean =>
  EVENT_TRIGGER_ROUTINE_TOOL_NAMES.has(name);
